import { connect } from 'react-redux';
import AutoRecommendedAlertsPanel from '../../components/AutoRecommendedAlerts/AutoRecommendedAlertsPanel';
import {
  setAutoRecommendedAlertsModalVisibility,
  changeAutoAlertState,
  changeAutoAlertPriority,
  addAutoAlertFeedback,
  fetchAutoAlertHistory,
  selectAutoRecommendedAlert,
} from '../../actions/AutoRecommendedAlertsActions';
import {
  buildDashboardEnumOptions,
  capitalizeText,
} from '../../utils/dashboardEnums';

// Resolve a label from server options, mirroring DashboardModal.getOptionLabel.
// dashboard_enum_* options are shaped { label:<numeric>, value:<text> }; standard
// options (AlertState, ...) are shaped { value:<numeric>, DisplayName:<text> }.
const getOptionLabel = (options, optionType, value) => {
  if (!options || !options[optionType] || value == null) return null;
  const isDashboardEnum = optionType.startsWith('dashboard_enum_');
  const opt = options[optionType].find((o) =>
    isDashboardEnum ? o.label == value : o.value == value || o.code == value
  );
  if (!opt) return null;
  const result = isDashboardEnum ? opt.value : opt.DisplayName;
  if (
    optionType === 'dashboard_enum_priority' ||
    optionType === 'dashboard_enum_opr_state'
  ) {
    return capitalizeText(result);
  }
  return result;
};

// Build alert-status filter options from the distinct statuses present in the data.
const buildAlertStatusOptions = (raw, options) => {
  const seen = new Set();
  const out = [];
  (raw || []).forEach((a) => {
    if (a.alert_state == null) return;
    const v = parseInt(a.alert_state);
    if (seen.has(v)) return;
    seen.add(v);
    out.push({
      value: v,
      label: getOptionLabel(options, 'AlertState', a.alert_state) || String(v),
    });
  });
  return out.sort((x, y) => x.value - y.value);
};

const processAlerts = (raw, options) =>
  (raw || []).map((a) => ({
    // Ranking is provided by the backend.
    ranking: a.ranking != null ? Math.round(a.ranking) : '',
    alertId: a.alert_id,
    // event_priority is null for unprioritized alerts; the select otherwise falls back to
    // its first option (Low), so treat null as 0 (Low) for consistent colour and filtering.
    priorityValue: a.event_priority != null ? parseInt(a.event_priority) : 0,
    alertStatus: getOptionLabel(options, 'AlertState', a.alert_state) || '',
    alertStatusValue:
      a.alert_state != null ? parseInt(a.alert_state) : undefined,
    probability: parseFloat(a.alert_probability || 0),
    eventStateValue: a.event_state != null ? parseInt(a.event_state) : 0,
    comment: a.alert_comment || '',
    feedback: a.feedback || '',
  }));

const mapStateToProps = (state) => {
  const dashboard = state.leaksList?.dashboard || {};
  const options = state.leaksList?.optionList?.options;
  const slice = dashboard.autoRecommendedAlerts || { data: [], loading: false };
  const selectedFeature = state.leaksList?.leaksByProject?.selectedFeature;
  return {
    open: !!dashboard.autoRecommendedAlertsModalVisible,
    alerts: processAlerts(slice.data, options),
    loading: !!slice.loading,
    eventStateOptions: buildDashboardEnumOptions(
      options?.dashboard_enum_opr_state,
      'is_recomended_alerts'
    ),
    priorityOptions: buildDashboardEnumOptions(
      options?.dashboard_enum_priority
    ),
    alertStatusOptions: buildAlertStatusOptions(slice.data, options),
    history: dashboard.autoRecommendedAlertHistory || {
      data: [],
      loading: false,
    },
    highlightedAlertId: selectedFeature ?? null,
  };
};

const mapDispatchToProps = (dispatch) => ({
  onClose: () => dispatch(setAutoRecommendedAlertsModalVisibility(false)),
  onAlertClick: (alertId) => dispatch(selectAutoRecommendedAlert(alertId)),
  onChangeState: (alertId, newState, feedback) =>
    dispatch(changeAutoAlertState(alertId, newState, feedback)),
  onChangePriority: (alertId, newPriority) =>
    dispatch(changeAutoAlertPriority(alertId, newPriority)),
  onAddFeedback: (alertId, feedback) =>
    dispatch(addAutoAlertFeedback(alertId, feedback)),
  onFetchHistory: (alertId) => dispatch(fetchAutoAlertHistory(alertId)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AutoRecommendedAlertsPanel);
